import { html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  type LobbyCategoryDisplay,
  type LobbyGame,
  TileType,
} from '../../base/package/api/package/gaming/gaming-domain';
import style from './ui-user-area.css?inline';
import '../carousels/ui-games-carousel';
import '../ui-tabs/ui-tabs';

const CName = 'ui-user-area';

type UserAreaGameTypes = 'lastPlayed' | 'favorites' | 'similar';
export type UserAreaGame = {
  categoryTranslationKey: string;
  games: LobbyGame[];
  id: UserAreaGameTypes;
  title: string;
};

@customElement(CName)
export class UiUserArea extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: Array }) data: UserAreaGame[];
  @property({ type: Boolean }) isNested = false;
  @property({ type: String }) trackingSource?: string = undefined;

  @state() private _selectedTab?: string;
  @state() private _filteredCategories: UserAreaGame[];
  @state() private _tabs: string[];

  private readonly _display: LobbyCategoryDisplay = {
    limit: 20,
    rank: false,
    rows: 1,
    shouldShowLabels: true,
    tile: TileType.P1,
  };

  willUpdate(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('data')) {
      this._filteredCategories = this.data?.filter((category) => category.games?.length) || [];
      this._tabs = this._filteredCategories.map((category) => category.title);
      this._selectedTab = this._tabs[0];
    }
  }

  render() {
    return html`
      <div class=${`user-area ${this.isNested ? 'user-area--is-nested' : ''}`}>
        <ui-tabs
          .tabs=${this._tabs}
          .selectedTab=${this._selectedTab}
          .onTabClickCb=${(clickedTab: string) => this._selectTab(clickedTab)}
        ></ui-tabs>
        <div class="carousel-panels" data-testid=${this._selectedCarousel}>
          ${this._renderCarousel('lastPlayed')}
          ${this._renderCarousel('favorites')}
        </div>
      </div>
    `;
  }

  private _renderCarousel(carouselId: UserAreaGameTypes) {
    const isVisible = this._selectedCarousel === carouselId;
    const carouselGames =
      carouselId === 'lastPlayed' ? this._lastPlayedCategory?.games : this._favoritesCategory?.games;
    const trackingSource = this._currentTrackingSourceFor(carouselId);
    return html`
      <ui-games-carousel
        class=${isVisible ? nothing : 'hidden'}
        .trackingSource=${trackingSource}
        .categorySlug=${carouselId}
        .games=${carouselGames}
        .display=${this._display}
        padding="var(--ui-space-md)"
      ></ui-games-carousel>
    `;
  }

  private get _selectedCarousel(): UserAreaGameTypes {
    const category = this._filteredCategories.find((cat) => cat.title === this._selectedTab)!;
    return category.id;
  }

  private get _lastPlayedCategory(): UserAreaGame | undefined {
    return this.data?.find((category) => category.id === 'lastPlayed');
  }

  private get _favoritesCategory(): UserAreaGame | undefined {
    return this.data?.find((category) => category.id === 'favorites');
  }

  private _selectTab(tab: string) {
    this._selectedTab = tab;
  }

  private _currentTrackingSourceFor(carouselId: UserAreaGameTypes): string | undefined {
    if (!this.trackingSource) return this.trackingSource;
    // Keep tracking strings as is, even if it has typo.
    return `${this.trackingSource}-${carouselId.toLowerCase() === 'lastplayed' ? 'lastPlayed' : 'Favourites'}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiUserArea;
  }
}
